import { WebDisMetadata } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { globalAttributes } from '../common/global-attributes';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useActingUserHasDexDeviceOverride } from './use-acting-user-has-dex-device-override';
import { useDexMetadata } from './use-dex-metadata';

export type DexDeviceIdentificationState = {
    isCheckingDeviceIdentification: boolean;
    showDexDeviceIdentificationError: boolean;
};

export function useDexDeviceIdentificationErrorState(): DexDeviceIdentificationState {
    const { dexRegion } = useBootstrapState();
    const { isFirstResponder, isWeb, isElectron } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const { dexMockModeEnabled } = useEnabledFeatures();
    const { dexMetadata, isLoading: isDexMetadataLoading } = useDexMetadata();
    const bypassCheck =
        isElectron || dexMockModeEnabled || !dexRegion || dexRegion === 'LA_LLETS';
    const {
        hasDexDeviceOverride,
        isLoading: isHasDexDeviceOverrideLoading,
    } = useActingUserHasDexDeviceOverride({
        bypassCheck,
    });
    const shouldShowFirstResponderDeviceIdentificationError =
        !isFirstResponder ||
        unitAssignment?.unitAgencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
    const isDeviceIdentificationReady =
        bypassCheck || (!isDexMetadataLoading && !isHasDexDeviceOverrideLoading);
    const hasRegisteredDexDevice =
        !!(dexMetadata as WebDisMetadata | undefined)?.machineName || !!hasDexDeviceOverride;
    const shouldRunDeviceIdentificationCheck =
        isWeb && !bypassCheck && shouldShowFirstResponderDeviceIdentificationError;
    const isDeviceIdentificationError =
        shouldRunDeviceIdentificationCheck &&
        isDeviceIdentificationReady &&
        !hasRegisteredDexDevice;
    const isCheckingDeviceIdentification =
        !bypassCheck && (isDexMetadataLoading || isHasDexDeviceOverrideLoading);

    return {
        isCheckingDeviceIdentification,
        showDexDeviceIdentificationError: isDeviceIdentificationError,
    };
}
